import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent } from '@shopify/events';

class EuroCartVariant extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.select = this.querySelector('[data-euro-cart-variant-select]');
    this.button = this.querySelector('[data-euro-cart-variant-submit]');
    this.status = this.querySelector('[data-euro-cart-variant-status]');
    this.select?.addEventListener('change', () => this.syncButton());
    this.button?.addEventListener('click', () => this.swapVariant());
    this.syncButton();
  }

  syncButton() {
    if (!(this.select instanceof HTMLSelectElement) || !(this.button instanceof HTMLButtonElement)) return;
    this.button.disabled = this.select.value === this.dataset.currentVariant || this.dataset.state === 'busy';
  }

  async swapVariant() {
    if (!(this.select instanceof HTMLSelectElement) || !(this.button instanceof HTMLButtonElement)) return;
    const newVariantId = this.select.value;
    const currentVariantId = this.dataset.currentVariant || '';
    const lineKey = this.dataset.lineKey || '';
    const quantity = Number(this.dataset.quantity || 1);
    if (!newVariantId || !lineKey || newVariantId === currentVariantId) return;

    this.dataset.state = 'busy';
    this.button.disabled = true;
    if (this.status) this.status.textContent = this.dataset.busyText || '';

    const cartComponents = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'));
    const sectionIds = [...new Set(cartComponents.map((component) => component.dataset.sectionId).filter(Boolean))];
    const eventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: lineKey, quantity }],
        promise: eventPromise.promise,
      })
    );

    let addedLine = null;
    let previousTargetQuantity = 0;
    let phase = 'read cart';
    try {
      const cartBefore = await this.request(`${Theme.routes.cart_url}.js`);
      const targetBefore = cartBefore.items?.find(
        (item) => String(item.variant_id) === newVariantId && Object.keys(item.properties || {}).length === 0
      );
      previousTargetQuantity = targetBefore?.quantity || 0;

      phase = 'add new variant';
      await this.request(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(newVariantId), quantity }] }),
      });

      // Automatic discounts can recalculate line-item keys after an add. Read
      // the cart again so both the old and replacement lines use current keys.
      const cartAfterAdd = await this.request(`${Theme.routes.cart_url}.js`);
      const hasNoProperties = (item) => Object.keys(item.properties || {}).length === 0;
      const oldLineAfter = cartAfterAdd.items?.find(
        (item) => String(item.variant_id) === currentVariantId && hasNoProperties(item)
      );
      const targetLineAfter = cartAfterAdd.items?.find(
        (item) => String(item.variant_id) === newVariantId && hasNoProperties(item)
      );
      if (!oldLineAfter?.key || !targetLineAfter?.key) throw new Error('Cart lines could not be resolved after adding');
      addedLine = targetLineAfter;

      phase = 'remove old variant';
      const finalCart = await this.request(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: oldLineAfter.key,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      phase = 'refresh cart';
      for (const component of cartComponents) {
        const sectionId = component.dataset.sectionId;
        const html = finalCart.sections?.[sectionId];
        if (sectionId && html) morphSection(sectionId, html, { mode: component.isDrawer ? 'hydration' : 'full' });
      }

      phase = 'publish update';
      eventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(finalCart),
        detail: { sections: finalCart.sections, items: finalCart.items, source: 'euro-cart-variant', didError: false },
      });
      this.track('euro_cart_variant_changed', { from_variant: currentVariantId, to_variant: newVariantId });
    } catch (error) {
      if (addedLine?.key) await this.rollback(addedLine.key, previousTargetQuantity);
      eventPromise.reject(error);
      this.dataset.state = 'error';
      if (this.status) this.status.textContent = this.dataset.errorText || 'Unable to update this item.';
      this.button.disabled = false;
      console.error(`[euro-cart-variant] ${phase}`, error);
    }
  }

  async rollback(lineKey, quantity) {
    try {
      await this.request(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity }),
      });
    } catch (rollbackError) {
      console.error('[euro-cart-variant] rollback failed', rollbackError);
    }
  }

  async request(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) {
      throw new Error(data.description || data.message || data.errors || 'Cart update failed');
    }
    return data;
  }

  track(event, detail) {
    window.dataLayer?.push({ event, ...detail });
    window.Shopify?.analytics?.publish?.(event, detail);
  }
}

if (!customElements.get('euro-cart-variant')) customElements.define('euro-cart-variant', EuroCartVariant);
